/**
 * Clock face. Tenths appear under ten seconds — the same threshold chess.com
 * uses, and the point at which whole seconds stop being enough information.
 */
export const formatClock = (ms: number): string => {
  if (!Number.isFinite(ms)) return '∞'
  const clamped = Math.max(0, ms)
  const totalSeconds = clamped / 1_000

  if (clamped < 10_000) {
    const seconds = Math.floor(totalSeconds)
    const tenths = Math.floor((clamped % 1_000) / 100)
    return `${seconds}.${tenths}`
  }

  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.floor(totalSeconds % 60)
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export const isLowTime = (ms: number): boolean => Number.isFinite(ms) && ms < 20_000

export const formatMoveNumber = (ply: number): string => `${Math.floor(ply / 2) + 1}.`

const RELATIVE = [
  { limit: 60_000, divisor: 1_000, unit: 'second' },
  { limit: 3_600_000, divisor: 60_000, unit: 'minute' },
  { limit: 86_400_000, divisor: 3_600_000, unit: 'hour' },
  { limit: Number.POSITIVE_INFINITY, divisor: 86_400_000, unit: 'day' },
] as const

export const formatRelativeTime = (at: number, now: number): string => {
  const elapsed = Math.max(0, now - at)
  if (elapsed < 5_000) return 'just now'
  const scale = RELATIVE.find((entry) => elapsed < entry.limit) ?? RELATIVE[3]
  const value = Math.floor(elapsed / scale.divisor)
  return `${value} ${scale.unit}${value === 1 ? '' : 's'} ago`
}

export const formatTimeOfDay = (at: number): string =>
  new Date(at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

/**
 * A stored time control ("300+0", "unlimited") as a person reads it.
 *
 * The archive stores seconds because that is unambiguous; nobody says "I play
 * three hundred plus zero".
 */
export const formatTimeControl = (id: string): string => {
  if (id === 'unlimited' || id === '-') return 'No limit'
  const [initial, increment] = id.split('+').map(Number)
  if (initial === undefined || Number.isNaN(initial)) return id
  const minutes = initial / 60
  if (increment) return `${minutes} | ${increment}`
  return Number.isInteger(minutes) ? `${minutes} min` : `${initial}s`
}
